// src/components/Chart/MarketPerformanceChart.jsx
import React, { useState, useEffect, useRef } from 'react';
import { chartService } from '../../services/chartService';
import { useCurrency } from '../../context/CurrencyContext';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import './MarketPerformanceChart.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MarketPerformanceChart = ({ initialStockSymbol }) => {
  const [availableStocks, setAvailableStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('1hr');
  const [chartData, setChartData] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [apiCurrency, setApiCurrency] = useState('NGN'); // NEW: Track API currency
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  
  const { formatCurrency, getCurrencySymbol, currency } = useCurrency();

  const intervals = [
    { value: '1min', label: '1M' },
    { value: '5min', label: '5M' },
    { value: '15min', label: '15M' },
    { value: '1hr', label: '1H' },
    { value: '24hr', label: '1D' },
    { value: '1month', label: '1Mo' }
  ];

  useEffect(() => {
    fetchAvailableStocks();
    // Close dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // When availableStocks loads or initialStockSymbol changes, select the correct stock
    if (availableStocks.length > 0 && initialStockSymbol) {
      const foundStock = availableStocks.find(stock => 
        stock.symbol === initialStockSymbol
      );
      if (foundStock) {
        setSelectedStock(foundStock);
      } else if (availableStocks.length > 0) {
        // Fallback to first stock if initial symbol not found
        setSelectedStock(availableStocks[0]);
      }
    } else if (availableStocks.length > 0 && !selectedStock) {
      // If no initial symbol, use first stock
      setSelectedStock(availableStocks[0]);
    }
  }, [availableStocks, initialStockSymbol]);

  useEffect(() => {
    if (selectedStock) {
      fetchChartData();
      fetchCurrentPrice();
    }
  }, [selectedStock, selectedInterval]);

  // Refetch when currency changes
  useEffect(() => {
    const handleCurrencyChange = () => {
      if (selectedStock) {
        fetchChartData();
        fetchCurrentPrice();
      }
    };

    window.addEventListener('currencyChange', handleCurrencyChange);
    return () => window.removeEventListener('currencyChange', handleCurrencyChange);
  }, [selectedStock, selectedInterval]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowDropdown(false);
    }
  };

  const fetchAvailableStocks = async () => {
    try {
      setLoading(true);
      const stocks = await chartService.getAvailableStocks();
      setAvailableStocks(stocks);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    if (!selectedStock) return;
    try {
      const data = await chartService.getChartData(selectedStock.symbol, selectedInterval, 100);
      setChartData(data);
      // Store the currency from API response
      if (data.display_currency) {
        setApiCurrency(data.display_currency);
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
    }
  };

  const fetchCurrentPrice = async () => {
    if (!selectedStock) return;
    try {
      const price = await chartService.getCurrentPrice(selectedStock.symbol);
      setCurrentPrice(price);
      // Store the currency from API response
      if (price.display_currency) {
        setApiCurrency(price.display_currency);
      }
    } catch (err) {
      console.error('Error fetching current price:', err);
    }
  };

  // Format price using API currency instead of context currency
  const formatPriceWithCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    
    if (apiCurrency === 'NGN') {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    }
  };

  const filteredStocks = availableStocks.filter(stock =>
    stock.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stock.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const getChartConfig = () => {
    if (!chartData || !chartData.data || chartData.data.length === 0) {
      return null;
    }

    const labels = chartData.data.map(point => {
      const date = new Date(point.timestamp);
      if (selectedInterval === '1min' || selectedInterval === '5min' || selectedInterval === '15min') {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      } else if (selectedInterval === '1hr') {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      } else if (selectedInterval === '24hr') {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    });

    const prices = chartData.data.map(point => point.price);

    return {
      labels,
      datasets: [
        {
          label: selectedStock?.symbol,
          data: prices,
          borderColor: '#15A759',
          backgroundColor: 'rgba(21, 167, 89, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          pointHoverBackgroundColor: '#15A759',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
          borderWidth: 2
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => formatPriceWithCurrency(context.parsed.y)  // ✅ Use API currency
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 0,
          maxTicksLimit: 7
        }
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: (value) => formatPriceWithCurrency(value)  // ✅ Use API currency
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  if (loading) {
    return (
      <div className="market-performance">
        <div className="chart-loading">Loading chart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="market-performance">
        <div className="chart-error">Error loading chart: {error}</div>
      </div>
    );
  }

  const config = getChartConfig();

  return (
    <div className="market-performance">
      <div className="chart-header">
        <div className="chart-title-section">
          <h2>Market Performance</h2>
          {currentPrice && (
            <div className="price-info">
              <span className="current-price">{formatPriceWithCurrency(currentPrice.current_price)}</span>
              <span className={`price-change ${currentPrice.price_change.percent >= 0 ? 'positive' : 'negative'}`}>
                {currentPrice.price_change.percent >= 0 ? '+' : ''}
                {currentPrice.price_change.percent.toFixed(2)}%
              </span>
            </div>
          )}
        </div>

        <div className="chart-controls">
          {/* Stock Selector with Search */}
          <div className="stock-selector" ref={dropdownRef}>
            <button 
              className="selector-button"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <span>{selectedStock?.symbol || 'Select Stock'}</span>
              <span className="dropdown-arrow">▼</span>
            </button>
            
            {showDropdown && (
              <div className="stock-dropdown">
                <input
                  type="text"
                  placeholder="Search stocks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dropdown-search"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="stock-options">
                  {filteredStocks.map(stock => (
                    <button
                      key={stock.symbol}
                      className={`stock-option ${selectedStock?.symbol === stock.symbol ? 'active' : ''}`}
                      onClick={() => handleStockSelect(stock)}
                    >
                      <span className="stock-symbol">{stock.symbol}</span>
                      <span className="stock-name">{stock.name}</span>
                    </button>
                  ))}
                  {filteredStocks.length === 0 && (
                    <div className="no-results">No stocks found</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Interval Selector */}
          <div className="interval-selector">
            {intervals.map(interval => (
              <button
                key={interval.value}
                className={`interval-button ${selectedInterval === interval.value ? 'active' : ''}`}
                onClick={() => setSelectedInterval(interval.value)}
              >
                {interval.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="chart-container">
        {config ? (
          <Line data={config} options={chartOptions} />
        ) : (
          <div className="no-chart-data">No chart data available</div>
        )}
      </div>
    </div>
  );
};

export default MarketPerformanceChart;